import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FiSearch, FiShoppingCart } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconGavel,
  IconHeart,
  IconHistory,
  IconProfile,
  IconReward,
  IconShipping,
  IconTrophy
} from "../../assets/icons/icons";
import CustomImg from "../../components/CustomImg/CustomImg.component";
import ModalLanguage from "../../components/Modals/ModalLanguage/modalLanguage.component";
import ModalLogin from "../../components/Modals/ModalLogin/modalLogin.component";
import ModalSignup from "../../components/Modals/ModalSignup/modalSignup.component";
import "../../components/ThemeSelector/ThemeToggle.css";
import { fetchUserProfileIcon } from "../../models/user.model";
import { UserContext } from "../../services/AppContext/appProvider";
import { useTheme } from "../../services/ThemeContext";
import { saveQuestionEngagementLocalStorage } from "../../utilities/aiChat";
import { KEY_URL_SEARCH } from "../../utilities/constants";
import {
  getLocStorageOpenItemsInNewTab,
  setLocStorageOpenItemsInNewTab
} from "../../utilities/storage";
import { formatThousands, utilLogOut } from "../../utilities/utils";
import "./topNavBar.scss";

// Safe to do as prevScroll can be undefined
type CustomWindow = typeof window & {
  prevScroll?: number;
};

const TopNavBar: React.FC = () => {
  const { theme } = useTheme(); // Get the current theme

  const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState<boolean>(false);
  useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const searchIconRef = useRef<HTMLButtonElement>(null);
  const searchBoxRefMobile = useRef<HTMLDivElement>(null);
  const searchIconRefMobile = useRef<HTMLButtonElement>(null);
  const menuDropdownRefDesktop = useRef<HTMLUListElement>(null);
  const profileDropdownRefMobile = useRef<HTMLUListElement>(null);
  const menuDropdownRefMobile = useRef<HTMLUListElement>(null);
  const navDesktopRef = useRef<HTMLDivElement>(null);
  const navMobileRef = useRef<HTMLDivElement>(null);

  const navTabletRef = useRef<HTMLDivElement>(null);
  const menuDropdownRefTablet = useRef<HTMLUListElement>(null);
  const searchBoxRefTablet = useRef<HTMLDivElement>(null);
  const searchIconRefTablet = useRef<HTMLButtonElement>(null);

  const [showMenuDropdownDashboard, setShowMenuDropdownDashboard] =
    useState<boolean>(false);
  const menuDropdownRefDashboard = useRef<HTMLUListElement>(null);

  // const [showChatNotificationsModal, setShowChatNotificationsModal] =
  //   useState<boolean>(false);
  // const [messageReceived, setMessageReceived] = useState<boolean>(false);

  const nav = useNavigate();
  const location = useLocation();
  const {
    isLoggedIn,
    numBids,
    showLoginModal,
    setShowLoginModal,
    cartItems,
    showSignupModal,
    setShowSignupModal,
    user,
    currentPage,
    showChatBox,
    setShowChatBox,
    aiSystemMessage,
    setAiSystemMessage,
    setNotificationPositionTop,
    setAiMessages,
    showChatNotificationModal,
    setShowChatNotificationModal,
    timeoutRef,
    hideNotificationTimeOutRef
  } = useContext(UserContext);
  const { t } = useTranslation();

  const handleClickOutside = (event: MouseEvent) => {
    if (
      navDesktopRef.current &&
      window.getComputedStyle(navDesktopRef.current).display !== "none" &&
      menuDropdownRefDesktop.current &&
      !menuDropdownRefDesktop.current.contains(event.target as Node) &&
      !searchBoxRef.current?.contains(event.target as Node) &&
      !searchIconRef.current?.contains(event.target as Node)
    ) {
      setShowMenuDropdown(false);
    }

    if (
      menuDropdownRefDesktop.current &&
      navDesktopRef.current &&
      window.getComputedStyle(navDesktopRef.current).display !== "none" &&
      !menuDropdownRefDesktop.current.contains(event.target as Node)
    ) {
      setShowMenuDropdown(false);
    }

    if (
      navMobileRef.current &&
      window.getComputedStyle(navMobileRef.current).display !== "none" &&
      profileDropdownRefMobile.current &&
      !profileDropdownRefMobile.current.contains(event.target as Node)
    ) {
    }
  };

  // Clears timers for AI Notifications
  // timeoutRef - controls popup delay time
  // hideNotificationTimeOutRef - controls time popup is visible
  const clearAITimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hideNotificationTimeOutRef.current) {
      clearTimeout(hideNotificationTimeOutRef.current);
      hideNotificationTimeOutRef.current = null;
    }
  };

  const handleScroll = () => {
    setIsScrolled(window.scrollY > 0);

    const customWindow = window as CustomWindow;
    if (
      window.scrollY <= 0 ||
      window.scrollY < (customWindow.prevScroll ?? 0)
    ) {
      setIsNavbarVisible(true);
      setNotificationPositionTop(false);
    } else {
      setIsNavbarVisible(false);
      setNotificationPositionTop(true);
    }
    customWindow.prevScroll = window.scrollY;
  };

  const handleNotificationClick = () => {
    // click on show chat box means question engagement
    clearAITimers();
    if (aiSystemMessage && aiSystemMessage.trim() !== "") {
      saveQuestionEngagementLocalStorage(currentPage, aiSystemMessage);
      setAiMessages([
        {
          system: true,
          ai: true,
          content: t(aiSystemMessage),
          time: Date.now()
        }
      ]);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    setShowChatNotificationModal(false);
    setShowChatBox(!showChatBox);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);
    const customWindow = window as CustomWindow;
    customWindow.prevScroll = window.scrollY;
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setShowMenuDropdown(false);
    setShowCategoriesDropdown(false);
  }, [location.pathname]);

  useEffect(() => {
    // get from session storage
    const profileImageSession = localStorage.getItem("profileImage");
    if (profileImageSession) {
      setProfileImage(profileImageSession);
    }

    const fetchProfileImage = async () => {
      try {
        const response = await fetchUserProfileIcon();
        if (response.rStatus === "success") {
          if (!response.rData.profileImage) {
            setProfileImage(null);
            localStorage.removeItem("profileImage");
            return;
          }
          setProfileImage(response.rData.profileImage);
          // set local storage
          localStorage.setItem("profileImage", response.rData.profileImage);
        } else {
          if (response.message === "profileImage is empty") {
            return;
          }
          console.error("Error fetching profile image:", response.message);
        }
      } catch (error) {
        console.error("Error fetching profile image:", error);
      }
    };

    if (isLoggedIn) {
      fetchProfileImage();
    }
  }, [isLoggedIn]);
  const isActiveLink = (path: string) => location.pathname === path;

  interface ProfileData {
    profileIcon: string;
  }

  const profileData: ProfileData = {
    profileIcon: "/profile/icon"
  };

  function handleLogout() {
    utilLogOut();
  }

  function handleSearch() {
    if (searchValue.trim() !== "") {
      const urlSafeSearchValue = encodeURIComponent(searchValue.trim());
      navScroll(`/search?${KEY_URL_SEARCH}=${urlSafeSearchValue}`);
      setShowSearch(false);
    }
  }

  const handleMenuLinkClick = (path: string) => {
    setShowMenuDropdown(false);
    setShowCategoriesDropdown(false);
    setShowHamburgerMenuDropdown(false);
    setShowMenuDropdownDashboard(false);
    navScroll(path);
  };

  const [itemsInNewTab, setItemsInNewTab] = useState<boolean>(
    getLocStorageOpenItemsInNewTab()
  );

  function openItemsInNewTabToggle() {
    setLocStorageOpenItemsInNewTab(!itemsInNewTab);
    setItemsInNewTab(!itemsInNewTab);
  }

  const [showHamburgerMenuDropdown, setShowHamburgerMenuDropdown] =
    useState<boolean>(false);

  // ref for mobile search
  const searchBoxRefMobileInput = useRef<HTMLInputElement>(null);

  // ref for desktop search
  const searchBoxRefInput = useRef<HTMLInputElement>(null);

  // ref for tablet search
  const searchBoxRefTabletInput = useRef<HTMLInputElement>(null);

  function showHideSearch() {
    console.log("showSearch", showSearch);
    if (showSearch) {
      setShowSearch(false);
      return;
    }

    setShowSearch(true);

    if (
      navDesktopRef.current &&
      searchBoxRefInput.current &&
      window.getComputedStyle(navDesktopRef.current).display !== "none"
    ) {
      console.log("desktop");
      // set focus on input
      searchBoxRefInput.current.focus();
      return;
    }

    if (
      navMobileRef.current &&
      searchBoxRefMobileInput.current &&
      window.getComputedStyle(navMobileRef.current).display !== "none"
    ) {
      console.log("mobile");
      // set focus on input
      searchBoxRefMobileInput.current.focus();
      return;
    }

    if (
      navTabletRef.current &&
      searchBoxRefTabletInput.current &&
      window.getComputedStyle(navTabletRef.current).display !== "none"
    ) {
      console.log("tablet");
      // set focus on input
      searchBoxRefTabletInput.current.focus();
      return;
    }

    console.log("no ref found");
  }

  function navScroll(link: string) {
    nav(link);
    window.scrollTo(0, 0);
  }

  function isDashboardLinkActive(page: string) {
    const urlPage = new URLSearchParams(location.search).get("page");

    if (page === "profile") {
      if (urlPage === "profile") {
        return true;
      }
      if (!urlPage) {
        return isActiveLink("/dashboard");
      }
    }

    console.log("urlPage", urlPage);
    console.log("page", page);
    // http://localhost:3007/dashboard?page=shipping
    return urlPage === page;
  }

  return (
    <>
      {/*======================================== Desktop navbar ================================================*/}
      <nav
        className={`navbar_desktop sticky top-0 z-50 hidden w-full transition-all duration-300 lg:flex ${
          isScrolled ? "bg-opacity-80" : "bg-opacity-100"
        } flex items-center py-1 pl-4 pr-0 backdrop-blur ${theme === "dark" ? "bg-success !text-white" : "bg-white !text-neutral-800"}`}
        ref={navDesktopRef}
      >
        <div className="mr-6 flex items-center gap-2">
          <img
            src={`../../assets/images/${theme === "dark" ? "LogoDark2.svg" : "LogoDark2.svg"}`}
            alt="logo"
            className={`btn btn-ghost btn-sm h-12 rounded-lg border-none hover:bg-transparent`}
            onClick={() => navScroll("/")}
          />
        </div>

        <div className="flex w-full items-center justify-between">
          <div
            className={`flex items-center justify-start gap-2 rounded-3xl bg-transparent ${theme === "dark" ? "!text-white" : "!text-neutral-800"}`}
          >
            {/* <div
              className="relative"
              onMouseEnter={() => setShowCategoriesDropdown(true)}
              onMouseLeave={() => setShowCategoriesDropdown(false)}
            >
              <div className="dropdown dropdown-end dropdown-hover h-full rounded-3xl p-3 px-5 text-lg hover:bg-secondary">
                <div
                  tabIndex={0}
                  className="link flex items-center text-base-100 no-underline hover:text-base-100"
                >
                  <FaCaretDown className="mr-1 inline-block h-4 w-4" />
                  {t("Categories")}
                </div>
                {showCategoriesDropdown && (
                  <ul
                    ref={menuDropdownRefDesktop}
                    className="dropdown-desktop menu dropdown-content absolute left-1/2 z-50 mt-2 w-52 -translate-x-1/2 transform rounded-box border-2 border-secondary bg-success bg-opacity-100 p-2 text-base-100 shadow-xl"
                  >
                    {productDepartments.length <= 0 ? (
                      <div className="container mx-auto">
                        <div className="text-center">
                          <span className="loading loading-ring"></span>
                          <span className="loading loading-ring"></span>
                          <span className="loading loading-ring"></span>
                        </div>
                      </div>
                    ) : (
                      productDepartments.map((department) => (
                        <li key={department.id}>
                          <button
                            className={`link no-underline ${
                              isActiveLink(`/category/${department.id}`)
                                ? "text-secondary"
                                : "text-base-100"
                            `}
                            onClick={() =>
                              handleMenuLinkClick(`/category/${department.id}`)
                            }
                          >
                            {department.department}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            </div> */}
            <button
              className={`rounded-3xl p-3 px-5 text-base-100 no-underline transition-all hover:bg-transparent hover:text-secondary ${
                isActiveLink("/")
                  ? "text-secondary"
                  : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
              }`}
              onClick={() => navScroll("/")}
            >
              {t("Home")}
            </button>
            <button
              className={`rounded-3xl p-3 px-5 text-base-100 no-underline transition-all hover:bg-transparent hover:text-secondary ${
                isActiveLink("/winners")
                  ? "text-secondary"
                  : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
              }`}
              onClick={() => navScroll("/winners")}
            >
              {t("Winners")}
            </button>

            <button
              className={`rounded-3xl p-3 px-5 text-base-100 no-underline transition-all hover:bg-transparent hover:text-secondary ${
                isActiveLink("/guide")
                  ? "text-secondary"
                  : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
              }`}
              onClick={() => navScroll("/guide")}
            >
              {t("Guide")}
            </button>

            <button
              className={`rounded-3xl p-3 px-5 text-base-100 no-underline transition-all hover:bg-transparent hover:text-secondary ${
                isActiveLink("/about")
                  ? "text-secondary"
                  : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
              }`}
              onClick={() => navScroll("/about")}
            >
              {t("About")}
            </button>
          </div>

          <div className="flex items-center gap-2 rounded-3xl p-0 pr-1">
            <div className="search-icon-container mx-4 mr-0 mt-2 lg:mx-5 lg:mr-0">
              <button
                className="search-icon"
                ref={searchIconRef}
                onClick={() => showHideSearch()}
                aria-label="Search"
              >
                <FiSearch
                  size={24}
                  className={`text-2xl ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                />
              </button>
            </div>

            {/* Search box */}
            <div className="search-box" ref={searchBoxRef}>
              <input
                ref={searchBoxRefInput}
                type="text"
                placeholder={t("Search")}
                className="nav-search input ml-2 mr-1 !min-w-0 max-w-max rounded-3xl border-2 border-base-100 align-middle text-neutral-800"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                style={{
                  width: showSearch ? "250px" : "0px",
                  opacity: showSearch ? "1" : "0",
                  padding: showSearch ? "0.5rem" : "0",
                  margin: showSearch ? "0" : "0",
                  border: showSearch ? "1px solid" : "0",
                  transition: "all 0.3s ease, opacity 1s ease"
                }}
              />
            </div>
            {/* Chat */}
            <div
              className={`${aiSystemMessage && "relative animate-pulse rounded-full p-1"}`}
            >
              <div
                className={`${aiSystemMessage && "absolute inset-0 rounded-full border-4 border-secondary"}`}
              ></div>
              <div className="relative flex items-center justify-center">
                <img
                  className="h-8 min-h-8 w-8 min-w-8 cursor-pointer rounded-full"
                  src="/assets/images/Jayda_ai.webp"
                  alt="ai_chat_image"
                  onClick={handleNotificationClick}
                />
              </div>
            </div>

            {!isLoggedIn ? (
              <>
                {/* <ThemeToggle variant="navbar" isLoggedIn={isLoggedIn} /> */}
                <button
                  className="btn btn-secondary btn-sm ml-2 rounded-lg border-none text-base-100"
                  onClick={() => setShowLoginModal(true)}
                >
                  {t("Login")}
                </button>

                <button
                  className="btn btn-secondary btn-sm ml-2 rounded-lg border-none text-base-100"
                  onClick={() => setShowSignupModal(true)}
                >
                  {t("Sign Up")}
                </button>
              </>
            ) : (
              <>
                <IconHeart
                  className="link h-7 min-h-7 w-7 min-w-7 fill-red-500 transition-all hover:fill-red-600"
                  onClick={() => navScroll("/dashboard?page=favorites")}
                />

                <button
                  className={`link relative ml-2 h-8 w-8 ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                  title="Cart"
                  onClick={() => navScroll("/dashboard?page=payment")}
                >
                  <FiShoppingCart className="h-8 w-8" />
                  {cartItems && cartItems.length > 0 && (
                    <span
                      className={`absolute right-1 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-xs text-white`}
                    >
                      {cartItems.length}
                    </span>
                  )}
                </button>
                <button
                  className={`btn btn-outline btn-secondary btn-sm -mr-3 ml-2 h-full rounded-lg ${theme === "dark" ? "hover:!text-neutral-800" : "hover!text-white"}`}
                  onClick={() => navScroll("/buy-packs")}
                >
                  {formatThousands(numBids)} {t("Bids")}
                </button>
                {/* Hamburger menu with dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setShowHamburgerMenuDropdown(true)}
                  onMouseLeave={() => setShowHamburgerMenuDropdown(false)}
                >
                  <label
                    tabIndex={0}
                    className="avatar btn btn-circle btn-ghost btn-sm ml-3 mr-0 rounded-lg border-none align-middle"
                    onClick={() => navScroll("/dashboard")}
                  >
                    <CustomImg
                      imgSrc={profileImage || "/profile/icon"}
                      alt="Profile"
                      className="rounded-full object-cover shadow-2xl shadow-neutral"
                      loadingClass="h-fit content-center rounded-full"
                      errorSource="/assets/images/icons/profile-solid.webp"
                    />
                  </label>

                  {showHamburgerMenuDropdown && (
                    <ul
                      ref={menuDropdownRefDesktop}
                      className={`menu dropdown-content absolute right-0 z-50 w-52 rounded-box border-2 border-none bg-opacity-100 p-2 shadow-xl ${theme === "dark" ? "bg-success text-base-100" : "bg-white text-neutral-800"}`}
                      style={{ textAlign: "right" }}
                    >
                      {/* Links to the dashboard page */}
                      <div className="flex flex-col">
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() => handleMenuLinkClick("/")}
                          >
                            {t("Home")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() => handleMenuLinkClick("/buy-packs")}
                          >
                            {t("Buy Blitz")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick(
                                "/dashboard?page=openAuctions"
                              )
                            }
                          >
                            {t("Open Auctions")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=history")
                            }
                          >
                            {t("Bids History")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=payment")
                            }
                          >
                            {t("Cart")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=winnings")
                            }
                          >
                            {t("Order History")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=shipping")
                            }
                          >
                            {t("Shipping")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=rewards")
                            }
                          >
                            {t("Rewards")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=history")
                            }
                          >
                            {t("Bids History")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=favorites")
                            }
                          >
                            {t("Favourites")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={() =>
                              handleMenuLinkClick("/dashboard?page=profile")
                            }
                          >
                            {t("Profile")}
                          </span>
                        </li>
                        <li>
                          <span
                            className="hover:text-secondary"
                            onClick={handleLogout}
                          >
                            {t("Logout")}
                          </span>
                        </li>
                      </div>

                      <div className="mx-auto my-5 h-[2px] w-[80%] justify-center rounded-full bg-secondary"></div>

                      {/* Other menu items */}
                      <div className="mb-5 ml-6 flex w-32 flex-col">
                        <li className="mb-5">
                          <label className="flex items-center justify-end">
                            <input
                              type="checkbox"
                              className="checkbox-secondary checkbox checkbox-xs mr-2"
                              checked={itemsInNewTab}
                              onChange={openItemsInNewTabToggle}
                            />
                            <span>{t("Open items in new tab")}</span>
                          </label>
                        </li>

                        {/* <li className="flex justify-end">
                          <ThemeToggle variant="dropdown" isLoggedIn={true} />
                        </li> */}
                      </div>
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </nav>

      {/*======================================= Mobile Navbar =================================================*/}
      <nav
        className={`navbar_mobile sticky top-0 flex w-full transition-all duration-300 md:hidden ${
          isScrolled ? "bg-opacity-80" : "bg-opacity-100"
        } flex items-center px-0 py-2 backdrop-blur md:px-2 ${
          isNavbarVisible ? "translate-y-0" : "-translate-y-full"
        } ${theme === "dark" ? "bg-success text-white" : "bg-white text-neutral-800"}`}
        ref={navMobileRef}
      >
        <div className="flex w-full flex-col">
          {/* Top Nav Bar */}
          <div className="flex w-full items-center">
            <div>
              <input
                type="checkbox"
                id="menu-toggle-mobile"
                className="hidden"
              />
              <label
                htmlFor="menu-toggle-mobile"
                className="btn btn-square btn-ghost btn-sm -mr-2 rounded-lg border-none md:mr-0"
                onClick={() => setShowMenuDropdown(!showMenuDropdown)}
              >
                <svg
                  className={`h-6 w-6 scale-x-[-1] ${theme === "dark" ? "text-white" : "text-neutral-800"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16m-7 6h7"
                  ></path>
                </svg>
              </label>
            </div>
            {!showSearch && (
              <div>
                <img
                  src={`../../assets/images/${theme === "dark" ? "LogoDark2.svg" : "LogoDark2.svg"}`}
                  alt="logo"
                  className={`btn btn-ghost btn-sm relative z-[51] ml-1 h-11 w-auto rounded-lg border-none p-3 py-3 pl-2 text-xl normal-case hover:bg-transparent md:pl-3`}
                  onClick={() => navScroll("/")}
                />
              </div>
            )}

            <div className="flex flex-1 items-center justify-end">
              <div
                className={`${
                  aiSystemMessage && "relative animate-pulse rounded-full p-1"
                }`}
              >
                <div
                  className={` ${aiSystemMessage && "absolute inset-0 rounded-full border-4 border-secondary"}`}
                ></div>
                <div className="relative flex items-center justify-center">
                  <img
                    className="h-8 min-h-8 w-8 min-w-8 cursor-pointer rounded-full"
                    src="/assets/images/Jayda_ai.webp"
                    alt="ai_chat_image"
                    onClick={handleNotificationClick}
                  />
                </div>
              </div>
              <div className="flex items-center gap-0 md:gap-2">
                <div className="search-icon-container mx-4 mr-0 mt-2 lg:mx-5 lg:mr-0">
                  <button
                    className="search-icon"
                    aria-label="Search"
                    ref={searchIconRefMobile}
                    onClick={() => showHideSearch()}
                  >
                    <FiSearch
                      size={24}
                      className={`text-2xl ${theme === "dark" ? "text-white" : "text-neutral-800"}`}
                    />
                  </button>
                </div>

                <div className="search-box" ref={searchBoxRefMobile}>
                  <input
                    ref={searchBoxRefMobileInput}
                    type="text"
                    placeholder={t("Search")}
                    className={`nav-search input ml-2 mr-1 !min-w-0 max-w-max rounded-3xl border-2 border-base-100 align-middle text-neutral-800 ${showSearch ? "opacity-1 w-48" : "w-0 opacity-0"}`}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    style={{
                      padding: showSearch ? "0.5rem" : "0",
                      margin: showSearch ? "0" : "0",
                      border: showSearch ? "1px solid" : "0",
                      transition: "all 0.3s ease, opacity 1s ease"
                    }}
                  />
                </div>
                {isLoggedIn && (
                  <div>
                    <label
                      tabIndex={0}
                      className="avatar btn btn-circle btn-ghost btn-sm ml-3 mr-0 aspect-square h-8 min-h-8 w-8 min-w-8 rounded-lg border-none align-middle"
                      onClick={() => navScroll("/dashboard")}
                    >
                      <CustomImg
                        imgSrc={profileImage || "/profile/icon"}
                        alt="Profile"
                        className="rounded-full object-cover"
                        loadingClass="h-fit content-center rounded-full"
                        errorSource="/assets/images/icons/profile-solid.webp"
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Mobile  Dropdown Menu */}
              {/* Overlay to stop page clicks when menu is open - click will close the mobile dropdown menu*/}
              {showMenuDropdown &&
                createPortal(
                  <div
                    className="fixed inset-0 z-40 bg-transparent"
                    onClick={() => setShowMenuDropdown(false)}
                  />,
                  document.body
                )}
              <div
                className={`menu dropdown-content absolute left-0 right-0 top-4 ${isLoggedIn ? "mt-12" : "mt-8"} p-2 shadow-lg ${
                  !showMenuDropdown ? "hidden" : ""
                } z-50 backdrop-blur transition-all duration-300 ${
                  theme === "dark"
                    ? "bg-success bg-opacity-100 text-white"
                    : "bg-white bg-opacity-100 text-neutral-800"
                }`}
              >
                <div className="flex flex-col justify-between">
                  {isLoggedIn && (
                    <div className="mb-7 flex items-center justify-center">
                      <div>
                        <label
                          tabIndex={0}
                          className="avatar btn btn-circle btn-ghost btn-sm ml-3 mr-0 aspect-square h-20 w-20 rounded-lg border-none align-middle"
                          onClick={() => navScroll("/dashboard")}
                        >
                          <CustomImg
                            imgSrc={profileImage || "/profile/icon"}
                            alt="Profile"
                            className="rounded-full object-cover"
                            loadingClass="h-fit content-center rounded-full"
                            errorSource="/assets/images/icons/profile-solid.webp"
                          />
                          {user?.nickname}
                        </label>
                      </div>
                    </div>
                  )}

                  <ul className="menu rounded-box">
                    <li onClick={() => handleMenuLinkClick("/")}>
                      <button
                        className={`link w-fit no-underline ${
                          isActiveLink("/")
                            ? "text-secondary"
                            : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                        }`}
                      >
                        {t("Home")}
                      </button>
                    </li>

                    {/* <li className="dropdown dropdown-end">
                  <label
                    tabIndex={0}
                    className="link text-base-100 no-underline hover:text-secondary"
                    onClick={() =>
                      setShowCategoriesDropdown(!showCategoriesDropdown)
                    }
                  >
                    {t("Categories")}
                    <FaCaretDown
                      className={`ml-1 ${
                        showCategoriesDropdown
                          ? "text-secondary"
                          : "text-base-100"
                      }`}
                    />
                  </label>
                  {showCategoriesDropdown && (
                    <ul
                      ref={menuDropdownRefMobile}
                      className="dropdown-mobile menu dropdown-content z-50 w-52 rounded-box border-2 border-secondary bg-success bg-opacity-100 p-2 text-base-100 shadow-xl"
                      style={{
                        left: "50%",
                        transform: "translateX(-50%)",
                        maxWidth: "calc(100vw - 2rem)"
                      }}
                    >
                      {productDepartments.map((department) => (
                        <li
                          key={department.id}
                          onClick={() =>
                            handleMenuLinkClick(`/category/${department.id}`)
                          }
                        >
                          <button
                            className={`link no-underline ${
                              isActiveLink(`/category/${department.id}`)
                                ? "text-secondary"
                                : "text-base-100"
                            }`}
                          >
                            {department.department}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li> */}

                    {isLoggedIn && (
                      // Dashboard Menu
                      <li className="dropdown dropdown-end w-full">
                        <button
                          className={`link no-underline ${
                            isActiveLink("/dashboard")
                              ? "text-secondary"
                              : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                          }`}
                          onClick={() => handleMenuLinkClick("/dashboard")}
                        >
                          {t("Dashboard")}
                        </button>

                        <ul
                          ref={menuDropdownRefDashboard}
                          className={`z-50 ml-0 w-full rounded-box border-none bg-none p-2 before:hidden ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                        >
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isActiveLink("/buy-packs")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() => handleMenuLinkClick("/buy-packs")}
                            >
                              <FiShoppingCart
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100 text-base-100" : "fill-neutral-800 text-neutral-800"}`}
                              />
                              {t("Buy Blitz")}
                            </button>
                          </li>
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("openAuctions")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick(
                                  "/dashboard?page=openAuctions"
                                )
                              }
                            >
                              <IconGavel
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100" : "fill-neutral-800"}`}
                              />
                              {t("Open Auctions")}
                            </button>
                          </li>
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("history")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick("/dashboard?page=history")
                              }
                            >
                              <IconHistory
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100" : "fill-neutral-800"}`}
                              />
                              {t("Bids History")}
                            </button>
                          </li>
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("payment")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick("/dashboard?page=payment")
                              }
                            >
                              <FiShoppingCart
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100 text-base-100" : "fill-neutral-800 text-neutral-800"}`}
                              />
                              {t("Cart")}
                            </button>
                          </li>
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("winnings")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick("/dashboard?page=winnings")
                              }
                            >
                              <IconTrophy
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100" : "fill-neutral-800"}`}
                              />
                              {t("Order History")}
                            </button>
                          </li>
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("shipping")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick("/dashboard?page=shipping")
                              }
                            >
                              <IconShipping
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100" : "fill-neutral-800"}`}
                              />
                              {t("Shipping")}
                            </button>
                          </li>
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("rewards")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick("/dashboard?page=rewards")
                              }
                            >
                              <IconReward
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100" : "fill-neutral-800"}`}
                              />
                              {t("Rewards")}
                            </button>
                          </li>

                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("favorites")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick("/dashboard?page=favorites")
                              }
                            >
                              <IconHeart
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100" : "fill-neutral-800"}`}
                              />
                              {t("Favourites")}
                            </button>
                          </li>
                          <li>
                            <button
                              className={`link w-fit no-underline ${
                                isDashboardLinkActive("profile")
                                  ? "text-secondary"
                                  : theme === "dark"
                                    ? "text-base-100"
                                    : "text-neutral-800"
                              }`}
                              onClick={() =>
                                handleMenuLinkClick("/dashboard?page=profile")
                              }
                            >
                              <IconProfile
                                className={`h-6 w-6 ${theme === "dark" ? "fill-base-100" : "fill-neutral-800"}`}
                              />
                              {t("Profile")}
                            </button>
                          </li>
                        </ul>
                      </li>
                    )}

                    <li>
                      <button
                        className={`link w-fit no-underline ${
                          isActiveLink("/winners")
                            ? "text-secondary"
                            : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                        }`}
                        onClick={() => handleMenuLinkClick("/winners")}
                      >
                        {t("Winners")}
                      </button>
                    </li>

                    <li onClick={() => handleMenuLinkClick("/guide")}>
                      <button
                        className={`link w-fit no-underline ${
                          isActiveLink("/guide")
                            ? "text-secondary"
                            : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                        }`}
                      >
                        {t("Guide")}
                      </button>
                    </li>

                    <li onClick={() => handleMenuLinkClick("/about")}>
                      <button
                        className={`link w-fit no-underline ${
                          isActiveLink("/about")
                            ? "text-secondary"
                            : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                        }`}
                      >
                        {t("About")}
                      </button>
                    </li>

                    {isLoggedIn && (
                      <li>
                        <button
                          className={`link w-fit no-underline ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                          onClick={handleLogout}
                        >
                          {t("Logout")}
                        </button>
                      </li>
                    )}

                    {!isLoggedIn && (
                      <>
                        <li>
                          <button
                            className={`link w-fit no-underline ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                            onClick={() => setShowLoginModal(true)}
                          >
                            {t("Login")}
                          </button>
                        </li>

                        <li>
                          <button
                            className={`link w-fit no-underline ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                            onClick={() => setShowSignupModal(true)}
                          >
                            {t("Sign Up")}
                          </button>
                        </li>
                      </>
                    )}
                  </ul>
                  <div className="mx-auto my-3 h-[1px] w-[80%] justify-center rounded-full bg-secondary"></div>
                  <div className="flex items-center justify-center">
                    <div className="">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="checkbox-secondary checkbox checkbox-xs mr-2"
                          checked={itemsInNewTab}
                          onChange={openItemsInNewTabToggle}
                        />
                        <span>{t("Open items in new tab")}</span>
                      </label>
                    </div>
                    {/* <div className="w-fit pl-4 text-left">
                  <ThemeToggle variant="dropdown" isLoggedIn={true} />
                </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Second Nav Bar */}
          <div className="mt-2 flex w-full flex-row items-center justify-center gap-2">
            {/* Not-Logged In Menu*/}
            {!isLoggedIn && (
              <div className="flex gap-3">
                <button
                  className="btn btn-outline btn-secondary btn-sm rounded-lg p-1 px-2 text-white"
                  onClick={() => setShowLoginModal(true)}
                >
                  {t("Login")}
                </button>
                <button
                  className="btn btn-outline btn-secondary btn-sm rounded-lg p-1 px-2 text-white"
                  onClick={() => setShowSignupModal(true)}
                >
                  {t("Sign Up")}
                </button>
                {/* Winners */}
                <button
                  className="btn btn-outline btn-secondary btn-sm rounded-lg p-1 px-2 text-white"
                  onClick={() => navScroll("/winners")}
                >
                  {t("Winners")}
                </button>
              </div>
            )}
            {/* Logged-In Menu */}
            {isLoggedIn && (
              <div className="flex items-center gap-3">
                {/* Cart */}
                <button
                  className={`link relative ml-1 h-8 w-8 ${theme === "dark" ? "text-white" : "text-neutral-800"}`}
                  onClick={() => navScroll("/dashboard?page=payment")}
                >
                  <FiShoppingCart className="h-8 w-8" />
                  {cartItems && cartItems.length > 0 && (
                    <span className="absolute right-1 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-xs text-white">
                      {cartItems.length}
                    </span>
                  )}
                </button>
                {/* Bids */}
                <button
                  className="btn btn-outline btn-secondary btn-sm h-full w-auto rounded-lg hover:!text-white"
                  onClick={() => navScroll("/buy-packs")}
                >
                  {formatThousands(numBids)} {t("Bids")}
                </button>
                {/* Winners */}
                <button
                  className="btn btn-outline btn-secondary btn-sm rounded-lg p-1 px-2 text-white"
                  onClick={() => navScroll("/winners")}
                >
                  {t("Winners")}
                </button>
                {/* Favourites */}
                <IconHeart
                  className="link h-7 fill-red-500 transition-all hover:fill-red-600"
                  onClick={() => navScroll("/dashboard?page=favorites")}
                />
              </div>
            )}
          </div>
        </div>
      </nav>

      {/*===================================== Tablet Navbar =================================================*/}
      <nav
        className={`navbar-large-tablet sticky top-0 z-50 hidden w-full transition-all duration-300 ${
          isScrolled ? "bg-opacity-80" : "bg-opacity-100"
        } flex items-center px-2 py-2 backdrop-blur md:flex lg:hidden ${
          isNavbarVisible ? "translate-y-0" : "-translate-y-full"
        } ${theme === "dark" ? "bg-success text-white" : "bg-white text-neutral-800"}`}
        ref={navTabletRef}
      >
        <div className="flex items-center gap-2">
          <img
            src={`../../assets/images/${theme === "dark" ? "LogoDark2.svg" : "LogoDark2.svg"}`}
            alt="logo"
            className={`btn btn-ghost btn-sm rounded-lg border-none p-3 py-0 text-xl normal-case hover:bg-transparent`}
            onClick={() => navScroll("/")}
          />
        </div>

        <div className="flex flex-1 items-center justify-end">
          <div className="flex items-center gap-2">
            {!isLoggedIn ? (
              <>
                {/* <ThemeToggle variant="navbar" isLoggedIn={isLoggedIn} /> */}
                <button
                  className="btn btn-secondary btn-sm ml-2 rounded-lg border-none"
                  onClick={() => setShowLoginModal(true)}
                >
                  {t("Login")}
                </button>

                <button
                  className="btn btn-secondary btn-sm ml-2 rounded-lg border-none text-base-100 hover:bg-primary hover:text-neutral"
                  onClick={() => setShowSignupModal(true)}
                >
                  {t("Sign Up")}
                </button>
              </>
            ) : (
              <>
                <IconHeart
                  className="link h-7 fill-red-500 transition-all hover:fill-red-600"
                  onClick={() => navScroll("/dashboard?page=favorites")}
                />

                <button
                  className={`link relative ml-1 h-8 w-8 ${theme === "dark" ? "text-white" : "text-neutral-800"}`}
                  onClick={() => navScroll("/dashboard?page=payment")}
                >
                  <FiShoppingCart className="h-8 w-8" />
                  {cartItems && cartItems.length > 0 && (
                    <span className="absolute right-1 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-xs text-white">
                      {cartItems.length}
                    </span>
                  )}
                </button>

                <button
                  className="btn btn-outline btn-secondary btn-sm ml-2 h-full rounded-lg border-2 hover:!text-white"
                  onClick={() => navScroll("/buy-packs")}
                >
                  {formatThousands(numBids)} {t("Bids")}
                </button>

                <div className="relative">
                  <label
                    tabIndex={0}
                    className="avatar btn btn-circle btn-ghost btn-sm rounded-lg border-none align-middle"
                    onClick={() => navScroll("/dashboard?page=profile")}
                  >
                    <CustomImg
                      imgSrc={profileImage || profileData.profileIcon}
                      alt="Profile"
                      className="rounded-full object-cover shadow-2xl shadow-neutral"
                      loadingClass="h-fit content-center rounded-full"
                      errorSource="/assets/images/icons/profile-solid.webp"
                    />
                  </label>
                </div>
              </>
            )}

            <div
              className={`${
                aiSystemMessage && "relative animate-pulse rounded-full p-1"
              }`}
            >
              <div
                className={` ${aiSystemMessage && "absolute inset-0 rounded-full border-4 border-secondary"}`}
              ></div>
              <div className="relative flex items-center justify-center">
                <img
                  className="h-8 min-h-8 w-8 min-w-8 cursor-pointer rounded-full"
                  src="/assets/images/Jayda_ai.webp"
                  alt="ai_chat_image"
                  onClick={handleNotificationClick}
                />
              </div>
            </div>
            <div className="search-icon-container mx-4 mr-0 mt-2 lg:mx-5 lg:mr-0">
              <button
                className="search-icon"
                aria-label="Search"
                ref={searchIconRefTablet}
                onClick={() => showHideSearch()}
              >
                <FiSearch
                  size={24}
                  className={`text-2xl ${theme === "dark" ? "text-white" : "text-neutral-800"}`}
                />
              </button>
            </div>

            <div className="search-box" ref={searchBoxRefTablet}>
              <input
                ref={searchBoxRefTabletInput}
                type="text"
                placeholder={t("Search")}
                className="nav-search input ml-2 mr-1 !min-w-0 max-w-max rounded-3xl border-2 border-base-100 align-middle text-neutral-800"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                style={{
                  width: showSearch ? "250px" : "0px",
                  opacity: showSearch ? "1" : "0",
                  padding: showSearch ? "0.5rem" : "0",
                  margin: showSearch ? "0" : "0",
                  border: showSearch ? "1px solid" : "0",
                  transition: "all 0.3s ease, opacity 1s ease"
                }}
              />
            </div>

            <input type="checkbox" id="menu-toggle-tablet" className="hidden" />
            <label
              htmlFor="menu-toggle-tablet"
              className="btn btn-square btn-ghost btn-sm rounded-lg border-none"
              onClick={() => setShowMenuDropdown(!showMenuDropdown)}
            >
              <svg
                className={`h-6 w-6 ${theme === "dark" ? "text-white" : "text-neutral-800"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16m-7 6h7"
                ></path>
              </svg>
            </label>
          </div>

          <div
            className={`menu dropdown-content absolute left-0 right-0 top-4 mt-12 p-2 shadow-lg ${
              !showMenuDropdown ? "hidden" : ""
            } z-50 backdrop-blur transition-all duration-300 ${
              theme === "dark"
                ? "bg-success bg-opacity-100 text-white"
                : "bg-white bg-opacity-100 text-neutral-800"
            }`}
          >
            <div className="flex justify-between">
              <ul className="menu w-1/2 rounded-box">
                <li onClick={() => handleMenuLinkClick("/")}>
                  <button
                    className={`link no-underline ${
                      isActiveLink("/")
                        ? "text-secondary"
                        : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                    }`}
                  >
                    {t("Home")}
                  </button>
                </li>

                {/* <li className="relative ml-2">
                  <div
                    className="categories-button"
                    onClick={() =>
                      setShowCategoriesDropdown(!showCategoriesDropdown)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <label
                      tabIndex={0}
                      className="link text-base-100 no-underline hover:text-secondary"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {t("Categories")}
                    </label>
                    <FaCaretDown
                      className={`ml-1 ${
                        showCategoriesDropdown
                          ? "text-secondary"
                          : "text-base-100"
                      }`}
                    />
                  </div>
                  {showCategoriesDropdown && (
                    <ul
                      ref={menuDropdownRefTablet}
                      className="dropdown-tablet menu dropdown-content z-50 w-52 rounded-box border-2 border-secondary bg-success bg-opacity-100 p-2 text-base-100 shadow-xl"
                    >
                      {productDepartments.map((department) => (
                        <li
                          key={department.id}
                          onClick={() =>
                            handleMenuLinkClick(`/category/${department.id}`)
                          }
                        >
                          <button
                            className={`link no-underline ${
                              isActiveLink(`/category/${department.id}`)
                                ? "text-secondary"
                                : "text-base-100"
                            }`}
                          >
                            {department.department}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li> */}

                {isLoggedIn && (
                  <li>
                    <button
                      className={`link no-underline ${
                        isActiveLink("/dashboard")
                          ? "text-secondary"
                          : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                      }`}
                      onClick={() => handleMenuLinkClick("/dashboard")}
                    >
                      {t("Dashboard")}
                    </button>
                  </li>
                )}

                <li onClick={() => handleMenuLinkClick("/winners")}>
                  <button
                    className={`link no-underline ${
                      isActiveLink("/winners")
                        ? "text-secondary"
                        : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                    }`}
                  >
                    {t("Winners")}
                  </button>
                </li>

                <li onClick={() => handleMenuLinkClick("/guide")}>
                  <button
                    className={`link no-underline ${
                      isActiveLink("/guide")
                        ? "text-secondary"
                        : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                    }`}
                  >
                    {t("Guide")}
                  </button>
                </li>

                <li onClick={() => handleMenuLinkClick("/about")}>
                  <button
                    className={`link no-underline ${
                      isActiveLink("/about")
                        ? "text-secondary"
                        : `${theme === "dark" ? "text-base-100" : "text-neutral-800"}`
                    }`}
                  >
                    {t("About")}
                  </button>
                </li>

                {isLoggedIn && (
                  <li>
                    <button
                      className={`link no-underline hover:text-secondary ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                      onClick={handleLogout}
                    >
                      {t("Logout")}
                    </button>
                  </li>
                )}

                {!isLoggedIn && (
                  <>
                    <li>
                      <button
                        className={`link no-underline hover:text-secondary ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                        onClick={() => setShowLoginModal(true)}
                      >
                        {t("Login")}
                      </button>
                    </li>

                    <li>
                      <button
                        className={`link no-underline hover:text-secondary ${theme === "dark" ? "text-base-100" : "text-neutral-800"}`}
                        onClick={() => setShowSignupModal(true)}
                      >
                        {t("Sign Up")}
                      </button>
                    </li>
                  </>
                )}
              </ul>

              <ul
                className="menu ml-auto flex w-auto flex-col items-center rounded-box"
                style={{ transform: "translateX(-90px)" }}
              >
                {isLoggedIn && (
                  <>
                    <li className="mt-3 flex w-full justify-center">
                      <button
                        className="btn btn-secondary btn-sm w-auto rounded-lg border-2 border-none border-secondary md:w-4/5 lg:w-4/5"
                        onClick={() => navScroll("/buy-packs")}
                      >
                        {formatThousands(numBids)} {t("Bids")}
                      </button>
                    </li>

                    <li className="mt-6 flex w-full justify-center">
                      <div className="flex w-full items-center justify-center md:w-4/5 lg:w-4/5">
                        <label className="flex items-center justify-center text-base-100">
                          <input
                            type="checkbox"
                            className="checkbox-secondary checkbox checkbox-xs"
                            checked={itemsInNewTab}
                            onChange={openItemsInNewTabToggle}
                          />
                          <span className="text-center">
                            {t("Open items in new tab")}
                          </span>
                        </label>
                      </div>
                    </li>

                    {/* <li className="mt-4 flex w-full justify-center">
                      <div className="flex w-full justify-center md:w-4/5 lg:w-4/5">
                        <ThemeToggle variant="dropdown" isLoggedIn={true} />
                      </div>
                    </li> */}
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </nav>

      {showLoginModal && (
        <ModalLogin
          closeModal={() => setShowLoginModal(false)}
          openRegistrationModal={() => setShowSignupModal(true)}
          modalType="modal"
        />
      )}

      {showSignupModal && (
        <ModalSignup
          closeModal={() => setShowSignupModal(false)}
          openLoginModal={() => setShowLoginModal(true)}
          modalType="modal"
        />
      )}

      {showLanguageModal && (
        <ModalLanguage closeModal={() => setShowLanguageModal(false)} />
      )}
    </>
  );
};

export default TopNavBar;
